/**
 * Sales Districts Operations - Hanterar visning och hantering av säljdistrikt på kartan
 * 
 * Använder district-cache.js för att hämta distrikt-data
 * Använder map-operations.js för att rendera på kartan
 */

import { generateFeatures } from '../utils/convert-to-geojson';
import * as mapOps from '../map/map-operations';
import { calculateDistrictData, calculateClassificationData } from '../districts/district-data-calculator';
import { applyFilters as applyFiltersToUnits } from '../filters/filter-applicator';
import { getSelectedPolygonFeatures } from '../districts/district-loader';
import { createHouseMarker } from '../drawing/drawing-operations';

const salesDistrictStyle = {
    strokeWeight: 0,
    strokeColor: 'transparent',
    fillOpacity: 0.3,
    visible: true,
    clickable: false,
    zIndex: 1
};

const homeMarkerInstances = new Map(); // Map<id, markerInstance>

/**
 * Ta bort en specifik home marker från Map
 * @param {string} districtId - Distrikt-ID för markern som ska tas bort
 */
export function removeHomeMarker(districtId) {
    const marker = homeMarkerInstances.get(districtId);
    if (marker) {
        marker.setMap(null);
        homeMarkerInstances.delete(districtId);
    }
}

/**
 * Lägg till en home marker i Map
 * @param {string} districtId - Distrikt-ID
 * @param {Object} marker - Marker-instans
 */
export function addHomeMarker(districtId, marker) {
    // Ta bort befintlig marker om den finns
    removeHomeMarker(districtId);
    homeMarkerInstances.set(districtId, marker);
}

export function getSelectedAreas(parsedLayers) {
    const parsedLayersKeys = Object.keys(parsedLayers);
    
    const selectedAreas = [];
    for (let i = 0; i < parsedLayersKeys.length; i++) {
        const layerType = parsedLayersKeys[i];
        if (parsedLayers[layerType].length > 0) {
            selectedAreas.push(...parsedLayers[layerType]);
        }
    }
    return selectedAreas;
}


export function getSelectedAreasForDistrict(parsedLayers) {
    const parsedLayersKeys = Object.keys(parsedLayers);
    
    const selectedAreasToUse = [];
    for (let i = 0; i < parsedLayersKeys.length; i++) {
        const layerType = parsedLayersKeys[i];
        if (parsedLayers[layerType].length > 0) {
            const selectedAreas = getSelectedPolygonFeatures(
                parsedLayers[layerType],
                layerType,
                mapOps
            );
            selectedAreasToUse.push(...selectedAreas);
        }
    }
    return selectedAreasToUse;
}

export function applyDistrictFiltersAndCalculate(district, classifications = []) {
    let filteredUnits = district.allUnits;
    
    if (district.salesDistrictFilters && district.allUnits) {
        filteredUnits = applyFiltersToUnits(
            district.allUnits,
            district.salesDistrictFilters,
            district.salesDistrictType.toLowerCase()
        );
    }

    const calculations = calculateDistrictData(
        district.selectedAreas,
        filteredUnits,
        district.salesDistrictFilters,
        district.allUnits
    );
    

    const classificationData = classifications.length > 0
        ? calculateClassificationData(
            classifications,
            calculations.units,
            district.salesDistrictType.toLowerCase(),
            district.allUnits
        )
        : { frequency: 0, vistingTimeHours: 0 };

    return {
        filteredUnits,
        calculations: {
            ...calculations,
            units: calculations.units.map(unit => unit.companyId),
            frequency: classificationData.frequency,
            vistingTimeHours: classificationData.vistingTimeHours
        }
    };
}

const hasSpatialGeoJson = (d) => {
    return d.spatialGeoJson && d.spatialGeoJson.length > 0;
}
const hasFeatures = (d) => {
    return generateFeatures(d.id, d.spatialGeoJson[0], 'salesdistrict') !== null;
}
const isVisible = (d) => {
    return d.visible === true;
}

const isCurrentDistrict = (d, currentDistrictId) => {
    return d.id === currentDistrictId*1; // Convert to number for comparison
}

// Filtrera distrikt för spatialGeoJson (exkluderar aktivt distrikt)
const filterDistricts = (d, currentDistrictId) => {
    return isVisible(d) && hasSpatialGeoJson(d) && hasFeatures(d) && !isCurrentDistrict(d, currentDistrictId);
}

// Filtrera distrikt för labels (inkluderar aktivt distrikt)
const filterDistrictsForLabels = (d, currentDistrictId) => {
    // Renderar labels även för aktivt distrikt
    return isVisible(d) && hasSpatialGeoJson(d) && hasFeatures(d);
}

const hasHomeMarkerGeoJson = (d) => {
    return d.homeMarkerGeoJson !== null && d.homeMarkerGeoJson !== '' && d.homeMarkerGeoJson !== undefined;
}

const filterHomeMarkers = (d, currentDistrictId) => {
    return isVisible(d) && hasHomeMarkerGeoJson(d) && !isCurrentDistrict(d, currentDistrictId);
}

// Features för spatialGeoJson (exkluderar aktivt distrikt)
const allFeatures = (districts, currentDistrictId) => {
    return districts.filter(d => filterDistricts(d, currentDistrictId)).map(d => generateFeatures(d.id, d.spatialGeoJson[0], 'salesdistrict', d.color));
}

// Features för labels (inkluderar aktivt distrikt)
const allFeaturesForLabels = (districts, currentDistrictId) => {
    return districts.filter(d => filterDistrictsForLabels(d, currentDistrictId)).map(d => {
        const feature = generateFeatures(d.id, d.spatialGeoJson[0], 'salesdistrict', d.color);
        if (feature) {
            feature.properties.name = d.name;
        }
        return feature;
    });
}

const allDistrictsWithHomeMarkers = (districts, currentDistrictId) => {
    return districts.filter(d => filterHomeMarkers(d, currentDistrictId));
}


/**
 * Visa säljdistrikt på kartan
 * @param {Object} params - Parameters
 * @param {Function} params.onLoadingChange - Callback för loading state
 * @param {string|null} params.currentDistrictId - ID för det nuvarande distriktet
 * @param {string|null} params.currentDistrictName - Name för det nuvarande distriktet
 * @param {string|null} params.currentDistrictColor - Färg för det nuvarande distriktet
 * @param {Function} params.getCachedSpatialInfo - Function to get cached spatial info
 */
export function show(allDistricts, currentDistrictId) {
    const map = mapOps.getMap();
    const maps = mapOps.getMaps();

    hide();

    // Features för spatialGeoJson (exkluderar aktivt distrikt)
    const featureCollection = {
        type: "FeatureCollection",
        features: allFeatures(allDistricts, currentDistrictId) 
    };

    map.data.addGeoJson(featureCollection);
    
    // Features för labels (inkluderar aktivt distrikt)
    // Använd separat featureCollection för labels så att labels renderas även för aktivt distrikt
    // men spatialGeoJson exkluderas (det aktiva distriktets spatialGeoJson renderas via selectedAreas)
    const featureCollectionForLabels = {
        type: "FeatureCollection",
        features: allFeaturesForLabels(allDistricts, currentDistrictId)
    };
    
    // Uppdatera mapState med sales districts för labels (inkluderar aktivt distrikt)
    mapOps.setSalesDistricts(featureCollectionForLabels);
    
    //Se till att sales districts har rätt styling
    featureCollection.features.forEach((f) => {
        const feature = map.data.getFeatureById(f.id);
        map.data.overrideStyle(feature, {
            ...salesDistrictStyle,
            fillColor: f.properties.color,
        });        
    });

    //Skapa home marker
    const districtsWithHomeMarkers = allDistrictsWithHomeMarkers(allDistricts, currentDistrictId);
    districtsWithHomeMarkers.forEach((f) => {
        const homeMarkerGeoJson = f.homeMarkerGeoJson;
        const center = new maps.LatLng(homeMarkerGeoJson.geometry.coordinates[1], homeMarkerGeoJson.geometry.coordinates[0]);
            
        const marker = createHouseMarker({
            maps,
            map,
            center,
            draggable: false,
            showCloseButton: false,
            style: { 
                backgroundColor: f.color
            }
        });

        homeMarkerInstances.set(f.id, marker);
    });

    mapOps.updateLabels();
}

/**
 * Dölj säljdistrikt från kartan
 */
export function hide() {
    const map = mapOps.getMap();
    
    homeMarkerInstances.forEach((marker, id) => {
        marker.setMap(null);
        homeMarkerInstances.delete(id);
    });

    map.data.forEach((feature) => {
        const featureType = feature.getProperty('type');
        if (featureType === 'salesdistrict') {
            map.data.remove(feature);
        }
    });

    // Rensa sales districts från mapState för labels
    mapOps.setSalesDistricts(null);
}

/**
 * Hämta aktuella sales districts
 * @returns {Object|null} Sales districts FeatureCollection eller null
 */
export function getSalesDistricts() {
    return mapOps.getSalesDistricts();
}
